/**
 * PATH: packages/email-hub/src/feature/components/category-filter-bar/fitCategoryChips.ts
 * FILE_ID: EMAILHUB-FEATURE-CATBAR-FIT-001
 * PURPOSE: Pure width-fit math for the category bar — priority, budget, compact tier.
 * WHY: The list pane resizes continuously (280-620px splitter + flex fill); any
 *      fixed chip cap clips at SOME width (operator escalations 07-11/07-13/07-16).
 *      Visible chips must be derived from measured widths, never constants.
 * RELATED: EMAILHUB-FEATURE-COMPONENTS-CATEGORYFILTERBAR-001
 */

import { CATEGORY_BAR_ORDER, CATEGORY_INLINE_MAX, CATEGORY_PINNED_PRIMARY } from '../../lib/categoryConfig'

/** Row gap between chips (matches the row's Tailwind `gap-1.5` = 6px). */
export const CATEGORY_BAR_GAP_PX = 6
/** Row horizontal padding (matches `px-3` = 12px each side). */
export const CATEGORY_BAR_PADDING_PX = 24
/** Sub-pixel / font-rounding head-room so a fitted row can never clip. */
export const CATEGORY_BAR_SAFETY_PX = 8

export interface CategoryBarFit {
  /** Chip keys that measurably fit inline, in priority order. */
  fitted: string[]
  /** Everything else — rendered in the More menu. */
  overflow: string[]
  /** True when even the full-form All/More/View controls don't fit. */
  compact: boolean
}

export interface CategoryBarMeasurements {
  /** clientWidth of the bar row (includes its own padding). */
  rowWidth: number
  allFull: number
  allCompact: number
  more: number
  viewFull: number
  viewIcon: number
  /** Natural width of every candidate chip, keyed by category key. */
  chipWidths: Record<string, number>
}

/**
 * Which categories exist on the bar at all: built-ins with volume or an
 * active selection, then custom personal./org. keys, then muted-only keys.
 * (Moved verbatim from the pre-split CategoryFilterBar.)
 */
export function buildVisibleKeys(
  byCategory: Record<string, number> | undefined,
  selectedCategories: string[],
  mutedCategories: string[],
): string[] {
  const builtinKeys = CATEGORY_BAR_ORDER.filter(
    (k) => selectedCategories.includes(k) || (byCategory?.[k] ?? 0) > 0,
  )
  const extraKeys = Object.keys(byCategory || {})
    .filter((k) => !CATEGORY_BAR_ORDER.includes(k))
    .filter(
      (k) =>
        selectedCategories.includes(k) ||
        mutedCategories.includes(k) ||
        (byCategory?.[k] ?? 0) > 0,
    )
    .filter((k) => k.startsWith('personal.') || k.startsWith('org.'))
    .sort()
  const mutedOnlyKeys = mutedCategories.filter(
    (k) => !builtinKeys.includes(k) && !extraKeys.includes(k),
  )
  return [...builtinKeys, ...extraKeys, ...mutedOnlyKeys]
}

/**
 * Inline priority: active selections first (the operator must see what they
 * filtered), then pinned money-flow, then the rest by volume. The measured
 * fit takes a prefix of this list, so order here IS the render order.
 */
export function buildChipPriority(
  visibleKeys: string[],
  byCategory: Record<string, number> | undefined,
  selectedCategories: string[],
): string[] {
  const selected = visibleKeys.filter((k) => selectedCategories.includes(k))
  const pinned = CATEGORY_PINNED_PRIMARY.filter(
    (k) => visibleKeys.includes(k) && !selected.includes(k),
  )
  const rest = visibleKeys
    .filter((k) => !selected.includes(k) && !pinned.includes(k))
    .sort((a, b) => (byCategory?.[b] ?? 0) - (byCategory?.[a] ?? 0))
  return [...selected, ...pinned, ...rest]
}

/**
 * Greedy prefix fit. Controls are reserved first (All + More + View always
 * render); chips consume the remainder in priority order. A chip that does
 * not fit STOPS the fill (no skip-fit — skipping reorders the row and reads
 * as a glitch). Compact tier flips when full-form controls alone overflow.
 */
export function computeCategoryBarFit(
  measurements: CategoryBarMeasurements,
  priority: string[],
  maxInline: number = CATEGORY_INLINE_MAX,
): CategoryBarFit {
  const available =
    measurements.rowWidth - CATEGORY_BAR_PADDING_PX - CATEGORY_BAR_SAFETY_PX
  const reservedFull =
    measurements.allFull +
    measurements.more +
    measurements.viewFull +
    2 * CATEGORY_BAR_GAP_PX
  const compact = reservedFull > available
  const reserved = compact
    ? measurements.allCompact +
      measurements.more +
      measurements.viewIcon +
      2 * CATEGORY_BAR_GAP_PX
    : reservedFull

  let budget = available - reserved
  const fitted: string[] = []
  for (const key of priority) {
    if (fitted.length >= maxInline) break
    const chipWidth = measurements.chipWidths[key]
    if (!chipWidth || chipWidth + CATEGORY_BAR_GAP_PX > budget) break
    fitted.push(key)
    budget -= chipWidth + CATEGORY_BAR_GAP_PX
  }
  return {
    fitted,
    overflow: priority.filter((k) => !fitted.includes(k)),
    compact,
  }
}

/** "34,393" normally; "34.4k" in the compact tier so the All pill fits 280px. */
export function formatChipCount(count: number, compact: boolean): string {
  if (!compact || count < 1000) return count.toLocaleString()
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(count)
    .toLowerCase()
}
